"use client";

import { useEffect, useState } from "react";
import { ArrowUpRight, Droplet, Leaf } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MarinelaHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [countdown, setCountdown] = useState({
    days: 14,
    hours: 6,
    minutes: 22,
    seconds: 48
  });
  useEffect(() => {
    const target = new Date();
    target.setDate(target.getDate() + 14);
    target.setHours(target.getHours() + 6);
    target.setMinutes(target.getMinutes() + 22);
    target.setSeconds(target.getSeconds() + 48);
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const distance = target.getTime() - now;
      if (distance < 0) {
        clearInterval(interval);
        return;
      }
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
      const minutes = Math.floor(distance % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(distance % (1000 * 60) / 1000);
      setCountdown({
        days,
        hours,
        minutes,
        seconds
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const pad = (n: number) => String(n).padStart(2, "0");
  const tickerItems = [{
    id: 1,
    label: "NEXT SHOWCASE IN",
    value: `${pad(countdown.days)}d ${pad(countdown.hours)}h ${pad(countdown.minutes)}m`,
    highlight: true
  }, {
    id: 2,
    label: "CREATIVE VISION",
    value: "",
    highlight: false
  }, {
    id: 3,
    label: "STRATEGIC DESIGN",
    value: "",
    highlight: false
  }, {
    id: 4,
    label: "ONLY",
    value: "2 SPOTS LEFT THIS SEASON",
    highlight: true
  }, {
    id: 5,
    label: "BY APPOINTMENT",
    value: "GLOBAL STUDIO",
    highlight: false
  }];
  const tickerLoop = [...tickerItems, ...tickerItems, ...tickerItems];
  return <section data-section-id="1384" id="brandpartnershero" className="relative overflow-hidden bg-background text-foreground py-28 md:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -left-4 top-1/2 -translate-y-1/2 select-none font-default font-light leading-none text-foreground/[0.04]" style={{
      fontSize: "14vw"
    }}>
        M
      </div>

      {}
      <Droplet aria-hidden="true" className="pointer-events-none absolute left-[6%] top-[18%] h-10 w-10 text-foreground/[0.08]" strokeWidth={1.5} />
      <Droplet aria-hidden="true" className="pointer-events-none absolute bottom-[14%] right-[8%] h-14 w-14 rotate-45 text-foreground/[0.08]" strokeWidth={1.5} />

      <div className="relative mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16">
        <div ref={ref} className={`grid grid-cols-1 gap-12 transition-all duration-700 ease-out lg:grid-cols-12 lg:gap-10 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
      }}>
          {}
          <div className="lg:col-span-8 flex flex-col">
            {}
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center font-default uppercase text-primary" style={{
              fontSize: "clamp(0.7rem, 0.8vw, 0.8125rem)",
              letterSpacing: "0.18em"
            }}>
                PORTFOLIO
              </span>
            </div>

            {}
            <div className="mt-5 flex items-center gap-2">
              <div className="h-px flex-1 bg-border md:flex-none md:w-40" />
              <Leaf className="h-3.5 w-3.5 text-secondary" strokeWidth={1.5} />
            </div>

            {}
            <h1 className="mt-8 font-default font-medium text-foreground" style={{
            fontSize: "clamp(2.75rem, 6.2vw, 5.5rem)",
            letterSpacing: "-0.025em",
            lineHeight: 0.98
          }}>Progetti. Brand. Idee</h1>

            {}
            <p className="mt-8 max-w-xl font-default font-normal text-muted-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            lineHeight: 1.55
          }}>
              Non un portfolio. Una storia di brand che ha cambiato direzione.
            </p>

            {}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild className="h-auto rounded-lg bg-primary px-7 py-3.5 font-default text-base font-semibold text-primary-foreground shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary/90">
                <Link to="/HomePage">
                  Start a project
                  <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={2} />
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-auto border-[1.5px] border-foreground bg-transparent px-7 py-3.5 font-default text-base font-semibold text-foreground transition-all duration-300 hover:bg-foreground hover:text-background rounded-lg">
                <Link to="/BlogPage">View work</Link>
              </Button>
            </div>

            {}
            <div className="mt-16 lg:mt-20">
              <div className="flex items-center gap-2">
                <span className="font-default uppercase text-secondary" style={{
                fontSize: "0.7rem",
                letterSpacing: "0.18em"
              }}>
                  ·ii — status
                </span>
                <div className="h-px flex-1 bg-border" />
                <Leaf className="h-3.5 w-3.5 text-secondary" strokeWidth={1.5} />
              </div>

              <div className="relative mt-5 overflow-hidden border-y border-border bg-card py-4 rounded-lg">
                <div className="flex w-max animate-[ticker-scroll_40s_linear_infinite] gap-10 whitespace-nowrap">
                  {tickerLoop.map((item, i) => <div data-index={i} key={`${item.id}-${i}`} className="flex items-center gap-3 font-default text-foreground" style={{
                  fontSize: "0.8125rem",
                  letterSpacing: "0.18em"
                }}>
                      <Droplet className="h-3 w-3 text-secondary" strokeWidth={1.5} fill="currentColor" />
                      <span className="uppercase">{item.label}</span>
                      {item.value && <span className={item.highlight ? "uppercase decoration-primary decoration-[3px] underline underline-offset-[6px]" : "uppercase"}>
                          {item.value}
                        </span>}
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="relative lg:col-span-4">
            <div className="relative">
              {}
              <div className="relative overflow-hidden bg-card rounded-lg">
                <div className="absolute inset-2 z-10 border border-border pointer-events-none rounded-lg" />
                <div className="aspect-[3/4] w-full">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721445/img/marinela_creative_studio_work.webp" alt="Creative design workspace with modern digital interface" loading="lazy" decoding="async" className="h-full w-full object-cover" src="https://lioniaproject.com/wp-content/uploads/2026/07/Screenshot-2025-05-09-alle-23.30.37.png" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721445/img/post_la-fotografia-come-linguaggio-strategico-non-solo-estetico.png" />
                </div>
              </div>

              {}
              <div className="absolute -top-6 right-4 md:-right-6 z-20 border border-border bg-background p-5 shadow-lg rounded-lg" style={{
              minWidth: "240px"
            }}>
                <div className="flex items-center gap-2">
                  <Droplet className="h-3 w-3 text-secondary" strokeWidth={1.5} fill="currentColor" />
                  <span className="font-default uppercase text-muted-foreground" style={{
                  fontSize: "0.7rem",
                  letterSpacing: "0.18em"
                }}>
                    opens in
                  </span>
                </div>

                <div className="mt-3 flex items-baseline gap-1 font-default font-medium text-foreground">
                  <CountdownDigit value={pad(countdown.days)} />
                  <span className="text-2xl text-muted-foreground">:</span>
                  <CountdownDigit value={pad(countdown.hours)} />
                  <span className="text-2xl text-muted-foreground">:</span>
                  <CountdownDigit value={pad(countdown.minutes)} />
                  <span className="text-2xl text-muted-foreground">:</span>
                  <CountdownDigit value={pad(countdown.seconds)} />
                </div>

                <div className="mt-3 h-[3px] w-full animate-[butter-pulse_2s_ease-in-out_infinite] bg-primary rounded-lg" />

                <div className="mt-3 flex items-center justify-between">
                  <span className="font-default uppercase text-muted-foreground" style={{
                  fontSize: "0.65rem",
                  letterSpacing: "0.15em"
                }}>
                    next season · q-release
                  </span>
                  <Leaf className="h-3 w-3 text-secondary" strokeWidth={1.5} />
                </div>
              </div>

              {}
              <div className="mt-5 flex items-center justify-between">
                <span className="font-default uppercase text-muted-foreground" style={{
                fontSize: "0.7rem",
                letterSpacing: "0.18em"
              }}>
                  ·iii — global studio
                </span>
                <span className="font-default text-muted-foreground" style={{
                fontSize: "0.7rem",
                letterSpacing: "0.12em"
              }}>
                  DESIGNED IN 2024
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @keyframes butter-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes digit-flip {
          0% { transform: translateY(-100%); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </section>;
}
function CountdownDigit({
  value
}: {
  value: string;
}) {
  return <span key={value} className="inline-block text-2xl md:text-3xl tabular-nums" style={{
    animation: "digit-flip 400ms cubic-bezier(0.22, 1, 0.36, 1)"
  }}>
      {value}
    </span>;
}